import * as React from 'react';

import { Block } from '@tableau/widgets';
import { ChooseProfilePictureDialog } from './ChooseProfilePictureDialog';
import { ImageBlobResizer } from './ImageBlobResizer';

const MAX_IMAGE_WIDTH = 200;
const MAX_IMAGE_HEIGHT = 200;
const MAX_BITS_PER_PIXEL = 24;
const MAX_SERVER_IMAGE_SIZE_LIMIT = MAX_IMAGE_WIDTH * MAX_IMAGE_HEIGHT * MAX_BITS_PER_PIXEL;

export class EditUserPhotoDialog extends React.Component<EditUserPhotoDialog.Props, EditUserPhotoDialog.State> {

  private inputElement: HTMLElement;
  private boundHandleImageChange: (event: any) => void;
  private boundHandleResizedFile: (resizedBlob: Blob) => void;
  private maxServerImageSizeLimit: number;

  public constructor(props: EditUserPhotoDialog.Props) {
    super(props);
    this.state = {
      rawSelectedBlob: null,
      imagePreviewUrl: this.props.imageUrl,
      resizedImageBlob: null
    };
    this.boundHandleImageChange = this.handleImageChange.bind(this);
    this.boundHandleResizedFile = this.handleResizedFile.bind(this);
    this.maxServerImageSizeLimit = this.props.maxServerImageSizeLimit || MAX_SERVER_IMAGE_SIZE_LIMIT;
  }

  public render(): JSX.Element {

    const hiddenInputElement =
      <input ref={input => this.inputElement = input}
             style={{ display: 'none' }}
             type="file"
             accept="image/*"
             onChange={this.boundHandleImageChange}
      />;
    return (
      <Block>
        {hiddenInputElement}
        <ImageBlobResizer
          inputBlob={this.state.rawSelectedBlob}
          handleResizeComplete={this.boundHandleResizedFile}
          maxImageSizeLimit={this.maxServerImageSizeLimit}
          maxWidth={MAX_IMAGE_WIDTH}
          maxHeight={MAX_IMAGE_HEIGHT}
        />
        <ChooseProfilePictureDialog
          handleChooseFromComputerButtonClick={() => this.inputElement.click()}
          imagePreviewUrl={this.state.imagePreviewUrl}
          profileDisplayName={this.props.profileDisplayName}
          previewContainsEdit={this.props.imageUrl!==this.state.imagePreviewUrl}
          onEditModeUpdate={this.props.onEditModeUpdate}
          handleSubmitRequest={() => this.props.handleSubmit(this.state.resizedImageBlob)}
        />
      </Block>
    );
  }

  // When user chooses new file for review
  private handleImageChange(event: React.FormEvent<HTMLInputElement>) {
    const inputBlob: Blob = (event.target as any).files[0];
    this.props.imageValidator(inputBlob, (isImageValidSizeAndFiletype: boolean) => {
      if (isImageValidSizeAndFiletype) {
          this.setState({ rawSelectedBlob: inputBlob });
      }
    });
  }

  // When child component finishes resizing raw inputBlob, read the resulting compressedBlob,
  // and save the dataUrl for preview, save the resizedBlob in case of file submit
  private handleResizedFile(resizedBlob: Blob) {
    if (!resizedBlob || !resizedBlob.size) return;
    const reader: FileReader = new FileReader();
    reader.onloadend = () => {
      this.setState({
        rawSelectedBlob: null,
        imagePreviewUrl: reader.result as string,
        resizedImageBlob: resizedBlob
      });
    };
    reader.readAsDataURL(resizedBlob);
  }
}

export module EditUserPhotoDialog {

  export interface Props {
    imageUrl: string;
    profileDisplayName: string;
    onEditModeUpdate: (inEditMode: boolean) => void;
    imageValidator: (inputFile: Blob, callback: (isValidImage: boolean) => void) => void;
    handleSubmit: (userImage: Blob) => void;
    maxServerImageSizeLimit?: number;
  }

  export interface State {
    rawSelectedBlob: Blob;   // contains file user selected via <input..>
    imagePreviewUrl: string; // holds dataUrl of the resized image when user selects a valid image file
    resizedImageBlob: Blob;  // saves the compressedBlob in case of file submit
  }

}
